import { Briefcase, FileDocument } from '@icims-design-system/icons';
import { Box, IconButton, Typography, useMediaQuery } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import React, { memo, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import ResponsiveSummaryWrapper from './ResponsiveSummaryWrapper';

const classes = {
	cardData: {
		height: 'max-content',
		marginRight: { xs: 'none', sm: 'none', md: '16px', lg: '16px', xl: '16px' },
		backgroundColor: 'background.paper',
		'& .Mui-expanded': {
			margin: '0 !important',
		},
	},
	accordian: {
		padding: 1,
	},
	content: {
		display: 'flex',
		alignItems: 'center',
		flexWrap: 'wrap',
	},
	iconWrapper: {
		width: '24px',
		height: '24px',
		borderRadius: '4px',
		border: '1px solid',
		borderColor: '#BDBDBD',
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
	},
};

const getContent = (title, value, marginTop) => {
	if (value) {
		return (
			<Box sx={{ marginTop }}>
				<Typography variant="body2">{title}</Typography>
				<Typography variant="body2" sx={{ fontWeight: 'bold' }}>
					{value}
				</Typography>
			</Box>
		);
	}
	return <></>;
};

const getContentWithIcon = (title, value, component, marginTop) => {
	if (value) {
		return (
			<Box sx={{ marginTop }}>
				<Box sx={classes.content}>
					<Typography variant="body2">{title}</Typography>
					<Box>{component}</Box>
				</Box>
				<Typography variant="body2" sx={{ fontWeight: 'bold' }}>
					{value}
				</Typography>
			</Box>
		);
	}
	return <></>;
};

const MetaDataCard = ({
	cardDetails,
	showJobDescPopover,
	setShowJobDescPopover,
	showResumePopover,
	setShowResumePopover,
}) => {
	const { t, i18n } = useTranslation(['candidate-feedback']);
	const theme = useTheme();
	const matches = useMediaQuery(theme.breakpoints.up('md'));
	const [expanded, setExpanded] = useState(!matches);

	useEffect(() => {
		if (matches) {
			setExpanded(true);
		} else {
			setExpanded((prevExpanded) => !prevExpanded);
		}
	}, [matches]);

	const date = new Intl.DateTimeFormat(i18n.language, {
		year: 'numeric',
		month: 'long',
		day: 'numeric',
	}).format(new Date(cardDetails.requestedOn));

	return (
		<Box role="group" sx={classes.cardData}>
			<Box
				sx={{
					...classes.accordian,
					borderBottom: matches ? 'none' : '1px solid rgb(238, 238, 238)',
				}}
			>
				<ResponsiveSummaryWrapper
					matches={matches}
					expanded={expanded}
					handleOnClick={() => setExpanded(!expanded)}
				>
					{getContentWithIcon(
						t('candidate'),
						cardDetails.candidate,
						<IconButton
							onClick={() => {
								setShowResumePopover(!showResumePopover);
							}}
							id={`resume-popover-button`}
							data-testid={`resume-popover-button`}
							sx={{ padding: 0.5, marginX: 1, color: 'text.secondary' }}
							aria-label={t('view_resume', { candidateName: cardDetails.candidate })}
						>
							<Box sx={classes.iconWrapper}>
								<FileDocument fontSize="small" sx={{ color: 'primary.main' }} />
							</Box>
						</IconButton>,
						matches ? '10px' : 0,
					)}
				</ResponsiveSummaryWrapper>
				{expanded && (
					<Box sx={{ paddingTop: 0, paddingX: 1 }} id="collapsable-content">
						{getContentWithIcon(
							t('job_title'),
							cardDetails.jobTitle,
							<IconButton
								onClick={() => {
									setShowJobDescPopover(!showJobDescPopover);
								}}
								id={`jobDescription-popover-button`}
								data-testid={`jobDescription-popover-button`}
								sx={{ margin: 0, padding: 0.5, marginX: 1, color: 'text.secondary' }}
								aria-label={t('view_job_description', { jobTitle: cardDetails.jobTitle })}
							>
								<Box sx={classes.iconWrapper}>
									<Briefcase fontSize="small" sx={{ color: 'primary.main' }} />
								</Box>
							</IconButton>,
							'10px',
						)}
						{getContent(t('requested_on'), date, '20px')}
						{getContent(t('interviewer'), cardDetails.interviewer, '20px')}
						{getContent(t('hiring_manager'), cardDetails.hiringManager, '20px')}
						{getContent(t('recruiter'), cardDetails.recruiter, '20px')}
					</Box>
				)}
			</Box>
		</Box>
	);
};

export default memo(MetaDataCard);
